import { ReactNode, useMemo } from "react";
import { Button, H5, Icon } from "@sellar-io/components";
import { formatCurrency, truncate } from "@sellar-io/common";
import { useLocation, useNavigate } from "react-router";
import { useLocalStorage, useMediaQuery } from "usehooks-ts";
import { Tooltip } from "@sellar-io/components";
import { TooltipContent } from "@sellar-io/components";
import { TooltipTrigger } from "@sellar-io/components";
import { setFallbackImage } from "../../../../components/image/imageWithFallback.shared";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { useDiscoveryV2ProductList } from "../../../../hooks/api/v2/discovery/useDiscoveryV2ProductList";
import {
  DISCOVER_SEARCH,
  UNIFIED_CHECKOUT_BASKET,
} from "../../../../constants/routes";
import { useMarketBasketFeedback } from "./useMarketBasketFeedback";
import { DiscoveryV2ListContainerTypeFilterType } from "@sellar-io/api-v2/types";
import { useDiscoveryV2SearchStore } from "../DiscoveryV2SearchStore";
import type { ConsolidatedBasketOpenSource } from "./consolidatedOrderingTracking";
import { useFloatingLayer } from "@sellar-io/components";

const RELATED_PRODUCTS_LIMIT = 4;

export const MarketBasketFlyover = ({
  children,
  onViewBasket,
}: {
  children: ReactNode;
  onViewBasket: (source: ConsolidatedBasketOpenSource) => void;
}) => {
  const { lastAddedItem, flyoverVisible, hideFlyover } =
    useMarketBasketFeedback();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const setSupplierId = useDiscoveryV2SearchStore(
    (state) => state.setSupplierId,
  );
  const setSelectedProductIds = useDiscoveryV2SearchStore(
    (state) => state.setSelectedProductIds,
  );
  const setSelectedSuppliers = useDiscoveryV2SearchStore(
    (state) => state.setSelectedSuppliers,
  );
  const setIsMySuppliersOnly = useDiscoveryV2SearchStore(
    (state) => state.setIsMySuppliersOnly,
  );
  const isDesktop = useMediaQuery("(min-width: 1084px)");
  const { business } = useSellarCurrentBusiness();
  const [unAuthorisedUserPostcode] = useLocalStorage<string | undefined>(
    "unauthorisedUserPostcode",
    undefined,
  );

  const buyerPostcode =
    business?.type === "retailer" ? business.DeliveryAddress.postalCode : "";
  const lastAddedContainerType = lastAddedItem?.containerType;
  const lastAddedSupplierId = lastAddedItem?.supplierId;

  const relatedProductsParams = useMemo(
    () => ({
      limit: RELATED_PRODUCTS_LIMIT,
      search: "",
      sortBy: "random" as const,
      unAuthorisedUserPostcode,
      buyerPostcode,
      query: [
        ...(lastAddedContainerType
          ? [
              {
                type: "containerType",
                modifier: "EQUALS",
                value: getContainerTypeKey(lastAddedContainerType),
              } as const,
            ]
          : []),
        ...(lastAddedSupplierId
          ? [
              {
                type: "supplierId",
                modifier: "IN",
                value: [lastAddedSupplierId] as number[],
              } as const,
            ]
          : []),
        {
          type: "inStockItemsOnly",
          modifier: "EQUALS",
          value: true,
        } as const,
      ],
    }),
    [
      lastAddedContainerType,
      lastAddedSupplierId,
      unAuthorisedUserPostcode,
      buyerPostcode,
    ],
  );

  const shouldLoadRelatedProducts =
    flyoverVisible &&
    isDesktop &&
    !!lastAddedItem?.supplierId &&
    !!lastAddedItem?.containerType;

  const relatedProductsQuery = useDiscoveryV2ProductList(
    relatedProductsParams,
    {
      enabled: shouldLoadRelatedProducts,
    },
  );

  const flyoverOpen =
    flyoverVisible &&
    isDesktop &&
    (!shouldLoadRelatedProducts || !relatedProductsQuery.isPending);

  const relatedProducts = useMemo(() => {
    const all = relatedProductsQuery.data?.pages.flat() ?? [];
    return all
      .filter((product) => product.id !== lastAddedItem?.productId)
      .slice(0, 3);
  }, [relatedProductsQuery.data, lastAddedItem?.productId]);

  const handleGoToCheckout = () => {
    if (!lastAddedItem?.sdBasketId) return;
    hideFlyover();
    navigate(
      UNIFIED_CHECKOUT_BASKET.href({
        sdBasketUuid: lastAddedItem.sdBasketId,
      }),
    );
  };

  const handleViewBasket = () => {
    hideFlyover();
    onViewBasket("flyover");
  };

  const handleRelatedProductClick = (product: {
    id: number;
    Parent?: { id: number } | null;
    Supplier: { id: number };
  }) => {
    hideFlyover();
    setSupplierId(product.Supplier.id);
    setSelectedProductIds({
      productItemId: product.id,
      parentProductId: product.Parent?.id,
    });
    navigate(DISCOVER_SEARCH);
  };

  const heroImageUrl = lastAddedItem
    ? setFallbackImage({
        img: lastAddedItem.heroImageUrl,
        fallback: "product-hero",
      })
    : "";

  const [productName, variantName] = (lastAddedItem?.name ?? "")
    .split("•")
    .map((s) => s.trim());

  const { popover } = useFloatingLayer();

  return (
    <Tooltip
      open={flyoverOpen}
      onOpenChange={(open) => {
        if (!open) hideFlyover();
      }}
      placement="bottom-end"
      dismissOnOutsidePress
    >
      <TooltipTrigger>{children}</TooltipTrigger>
      <TooltipContent
        transition={{
          duration: { open: 300, close: 200 },
          initial: { opacity: 0, transform: "translateY(-8px) scale(0.98)" },
        }}
        className={`${popover} w-97.5 rounded-lg border border-zinc-200 bg-white p-0 text-zinc-800 shadow-lg`}
      >
        {lastAddedItem && (
          <div className="flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between gap-3 rounded-t-lg border-b border-zinc-200 bg-zinc-50 p-4">
              <div className="flex items-center gap-2">
                <Icon
                  icon="CHECK_CIRCLE"
                  size={0.8}
                  className="text-purple-600"
                />
                <H5 className="text font-semibold text-zinc-800">
                  Added to basket
                </H5>
              </div>
              <button
                type="button"
                className="flex items-center text-zinc-700 hover:text-zinc-600"
                onClick={hideFlyover}
                aria-label="Dismiss"
              >
                <Icon icon="CLOSE" size={0.7} />
              </button>
            </div>

            <div className="p-4">
              <div className="flex items-center gap-3">
                {/* Product thumbnail */}
                <div
                  className="h-20 w-20 shrink-0 overflow-hidden rounded-lg bg-cover bg-center bg-no-repeat"
                  style={{ backgroundImage: `url(${heroImageUrl})` }}
                />

                {/* Name and description */}
                <div className="flex flex-1 flex-col gap-0.5">
                  <H5 className="font-semibold text-zinc-900">
                    {truncate(productName ?? "")}{" "}
                    <span className="font-normal">
                      / {lastAddedItem.supplierName}
                    </span>
                  </H5>
                  {variantName && (
                    <span className="text-sm text-zinc-600">{variantName}</span>
                  )}
                  <span className="mt-1 font-semibold text-zinc-800">
                    {formatCurrency(lastAddedItem.price ?? 0)}
                  </span>
                </div>
              </div>

              {/* CTAs */}
              <div className="mt-4 flex gap-2">
                <Button
                  className="flex-1 bg-white shadow-[0_2px_2px_0_rgba(0,0,0,0.15)]"
                  size="small"
                  color="secondary"
                  onClick={handleViewBasket}
                >
                  View basket
                </Button>
                <Button
                  className="flex-1 shadow-[0_2px_2px_0_rgba(0,0,0,0.15)]"
                  size="small"
                  onClick={handleGoToCheckout}
                >
                  Go to checkout
                </Button>
              </div>
            </div>

            {/* Related products */}
            {relatedProducts.length > 0 && (
              <div className="rounded-b-lg border-t border-zinc-200 bg-zinc-50 px-4 py-3">
                <button
                  type="button"
                  onClick={() => {
                    hideFlyover();
                    setSelectedProductIds(undefined);
                    setSelectedSuppliers([
                      {
                        id: lastAddedItem.supplierId,
                        name: lastAddedItem.supplierName,
                      },
                    ]);
                    setIsMySuppliersOnly(false);
                    if (pathname !== DISCOVER_SEARCH) {
                      navigate(DISCOVER_SEARCH);
                    }
                  }}
                  className="mb-2 cursor-pointer font-semibold text-zinc-800"
                >
                  More from {lastAddedItem.supplierName}
                  {" >"}
                </button>
                <div className="flex max-h-75 scrollbar-none flex-col gap-3 overflow-y-auto pb-1">
                  {relatedProducts.map((product) => {
                    const relatedHeroImageUrl = setFallbackImage({
                      img: product.Parent?.heroImageUrl ?? null,
                      fallback: "product-hero",
                    });
                    const [productName, variantName] = product.name?.split(
                      " • ",
                    ) ?? [product.name];
                    const productStyleAndAbv = [
                      product.Parent?.kind,
                      product.Parent?.abv != null
                        ? `${product.Parent.abv}%`
                        : undefined,
                    ]
                      .filter(Boolean)
                      .join(" • ");

                    return (
                      <button
                        type="button"
                        key={product.id}
                        className="flex w-full items-center gap-3 rounded-lg border border-zinc-200 bg-white px-4 py-3 text-left transition-colors hover:bg-zinc-100"
                        onClick={() => handleRelatedProductClick(product)}
                      >
                        {/* Product thumbnail */}
                        <div
                          className="h-13 w-13 shrink-0 overflow-hidden rounded-lg bg-cover bg-center bg-no-repeat"
                          style={{
                            backgroundImage: `url(${relatedHeroImageUrl})`,
                          }}
                        />

                        {/* Name and description */}
                        <div className="flex flex-1 flex-col gap-0.5">
                          <H5 className="font-semibold text-zinc-900">
                            {truncate(productName ?? "")}{" "}
                            <span className="font-normal">
                              /{" "}
                              {productStyleAndAbv || lastAddedItem.supplierName}
                            </span>
                          </H5>
                          {variantName && (
                            <span className="text-sm text-zinc-600">
                              {variantName}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
};

const getContainerTypeKey = (containerType: string) => {
  return containerType as DiscoveryV2ListContainerTypeFilterType;
};
